import {createAction} from 'redux-act';
import {ThunkAction, ThunkDispatch} from 'redux-thunk';
import {AnyAction} from 'redux';
import {StoreState} from './store';
import {ActionsDependencies} from './ActionsDependencies';

type Result<Success, Failure> = {type: 'success'; payload: Success} | {type: 'failure'; payload: Failure};

type ActionFn<Arg, Success, Failure> = (
	arg: Arg,
	meta: {
		getState(): StoreState;
		context: ActionsDependencies;
		dispatch: ThunkDispatch<StoreState, ActionsDependencies, AnyAction>;
		success(payload: Success): Result<Success, Failure>;
		failure(payload: Failure): Result<Success, Failure>;
	},
) => Promise<Result<Success, Failure>>;

/**
 * Helper to create asynchronous action without any boilerplate
 *
 * Inspired by "createAsyncThunk" from redux-toolkit
 * @see https://redux-toolkit.js.org/api/createAsyncThunk
 */
export function createAsyncThunk<Arg extends any = void, Success extends any = void, Failure extends any = void>(
	name: string,
	action: ActionFn<Arg, Success, Failure>,
) {
	const success = (payload: Success): Result<Success, Failure> => ({type: 'success', payload});
	const failure = (payload: Failure): Result<Success, Failure> => ({type: 'failure', payload});

	const handler = (
		arg: Arg,
	): ThunkAction<Promise<Result<Success, Failure>>, StoreState, ActionsDependencies, AnyAction> => {
		return async (dispatch, getState, context) => {
			dispatch(handler.request(arg));

			let result: Result<Success, Failure>;
			try {
				result = await action(arg, {getState, context, dispatch, success, failure});
			} catch (error) {
				// Handle unexpected failure
				dispatch(handler.failure());
				throw error;
			}

			if (result.type === 'success') {
				// Dispatch "success" action
				dispatch(handler.success(result.payload));
			} else {
				dispatch(handler.failure(result.payload));
			}

			return result;
		};
	};

	handler.request = createAction<Arg>(`${name}/request`);
	handler.failure = createAction<Failure | void>(`${name}/failure`);
	handler.success = createAction<Success>(`${name}/success`);

	return handler;
}
